import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorldState } from '../components/state/WorldStateContext.jsx';
import { AIRCRAFT_DATABASE, getAircraftImage, getAircraftImageSmart, getCollectibleImagePath } from '../data/aircraft.js';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Plane,
  Users,
  Gauge,
  DollarSign,
  MapPin,
  Clock,
  Wrench,
  ShoppingCart,
  Map,
  Activity,
  ChevronLeft,
  Fuel,
  Calendar,
  Gem
} from 'lucide-react';

export default function Fleet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { worldState } = useWorldState();

  const cash = worldState?.airline?.cash || 0;
  const aircraft = worldState?.aircraft || [];
  const leases = worldState?.leases || [];

  const formatMoney = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);

  // Get lease info for an aircraft
  const getLeaseInfo = (ac) => {
    if (!ac.isLeased) return null;
    return leases.find(l => l.visibleId === ac.registration || l.aircraftId === ac.id);
  };

  // Get aircraft specs from database
  const getAircraftSpecs = (ac) => {
    const specs = AIRCRAFT_DATABASE[ac.model] || AIRCRAFT_DATABASE[ac.aircraftType] || {};
    return specs;
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#0a0a0a', color: '#e5e5e5' }}>
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/opscenter")}
              style={{ color: "#737373" }}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t('fleet.opsCenter')}
            </Button>
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#60ffd0' }}>
                {t('fleet.title')}
              </h1>
              <p className="text-sm" style={{ color: '#737373' }}>{t('fleet.aircraftInFleet', { count: aircraft.length })}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="px-4 py-2 rounded" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" style={{ color: '#22c55e' }} />
                <span className="text-xl font-bold" style={{ color: '#22c55e' }}>{formatMoney(cash)}</span>
              </div>
            </div>

            <Button onClick={() => navigate("/Museum")} style={{ backgroundColor: '#a855f7', color: '#fff' }}>
              <Gem className="w-4 h-4 mr-2" /> {t('fleet.museum')}
            </Button>
            <Button onClick={() => navigate("/WorldMap")} style={{ backgroundColor: '#3b82f6', color: '#fff' }}>
              <Map className="w-4 h-4 mr-2" /> {t('fleet.worldMap')}
            </Button>
            <Button onClick={() => navigate("/opscenter")} style={{ backgroundColor: '#60ffd0', color: '#000' }}>
              <Activity className="w-4 h-4 mr-2" /> {t('fleet.opsCenter')}
            </Button>
          </div>
        </div>

        {/* Fleet Stats Summary */}
        {aircraft.length > 0 && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <Card style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold" style={{ color: '#60ffd0' }}>{aircraft.length}</div>
                <div className="text-xs" style={{ color: '#737373' }}>{t('fleet.totalAircraft')}</div>
              </CardContent>
            </Card>
            <Card style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold" style={{ color: '#22c55e' }}>
                  {aircraft.filter(a => !a.isLeased).length}
                </div>
                <div className="text-xs" style={{ color: '#737373' }}>{t('fleet.owned')}</div>
              </CardContent>
            </Card>
            <Card style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold" style={{ color: '#f59e0b' }}>
                  {aircraft.filter(a => a.isLeased).length}
                </div>
                <div className="text-xs" style={{ color: '#737373' }}>{t('fleet.leased')}</div>
              </CardContent>
            </Card>
            <Card style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold" style={{ color: '#3b82f6' }}>
                  {aircraft.filter(a => a.status === 'available').length}
                </div>
                <div className="text-xs" style={{ color: '#737373' }}>{t('fleet.available')}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Current Fleet */}
        <div className="mb-12">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#60ffd0' }}>{t('fleet.yourFleet')}</h2>

          {aircraft.length === 0 ? (
            <Card style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}>
              <CardContent className="py-12 text-center">
                <Plane className="w-16 h-16 mx-auto mb-4 opacity-30" style={{ color: '#737373' }} />
                <p className="text-lg mb-2" style={{ color: '#737373' }}>{t('fleet.noAircraft')}</p>
                <p className="text-sm mb-6" style={{ color: '#525252' }}>
                  {t('fleet.noAircraftHint')}
                </p>
                <Button
                  onClick={() => navigate("/AircraftMarket")}
                  style={{ backgroundColor: '#f59e0b', color: '#000' }}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" /> {t('fleet.browseMarket')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aircraft.map((ac) => {
                const specs = getAircraftSpecs(ac);
                const leaseInfo = getLeaseInfo(ac);
                
                return (
                  <Card 
                    key={ac.id} 
                    style={{ 
                      backgroundColor: '#0c0c0c', 
                      borderColor: ac.isLeased ? '#f59e0b' : '#1a1a1a',
                      borderWidth: ac.isLeased ? '2px' : '1px'
                    }}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Plane className="w-5 h-5" style={{ color: '#60ffd0' }} />
                          <span style={{ color: '#e5e5e5' }}>{ac.model || ac.aircraftType}</span>
                        </div>
                        <Badge
                          style={{
                            backgroundColor: ac.isLeased ? '#f59e0b20' : '#22c55e20',
                            color: ac.isLeased ? '#f59e0b' : '#22c55e',
                            border: `1px solid ${ac.isLeased ? '#f59e0b' : '#22c55e'}`
                          }}
                        >
                          {ac.isLeased ? t('fleet.leased') : t('fleet.owned')}
                        </Badge>
                      </CardTitle>
                      <div className="text-sm" style={{ color: '#737373' }}>
                        {ac.registration || ac.visibleId || ac.id}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Aircraft Image */}
                      <div
                        className="h-28 rounded mb-3 flex items-center justify-center overflow-hidden relative"
                        style={{ backgroundColor: '#111' }}
                      >
                        <img
                          src={getAircraftImageSmart(ac)}
                          alt={ac.model || ac.aircraftType || 'Aircraft'}
                          className="w-full h-full object-contain"
                          style={{ filter: 'drop-shadow(2px 4px 6px rgba(0,0,0,0.5))' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div style="color: #525252; font-size: 12px;">No image</div>';
                          }}
                        />
                        {/* Collectible Rarity Badge */}
                        {ac.rarity && (
                          <div
                            className="absolute top-1 right-1 px-2 py-0.5 rounded text-xs font-bold uppercase"
                            style={{
                              backgroundColor: ac.rarity === 'mythic' ? '#ff006680' :
                                              ac.rarity === 'ascendent' ? '#ff880080' :
                                              ac.rarity === 'legendary' ? '#f59e0b80' :
                                              ac.rarity === 'anomalous' ? '#a855f780' :
                                              ac.rarity === 'relic' ? '#cd7f3280' :
                                              '#3b82f680',
                              color: ac.rarity === 'mythic' ? '#ff0066' :
                                     ac.rarity === 'ascendent' ? '#ff8800' :
                                     ac.rarity === 'legendary' ? '#f59e0b' :
                                     ac.rarity === 'anomalous' ? '#a855f7' :
                                     ac.rarity === 'relic' ? '#cd7f32' :
                                     '#3b82f6',
                              border: `1px solid ${
                                ac.rarity === 'mythic' ? '#ff0066' :
                                ac.rarity === 'ascendent' ? '#ff8800' :
                                ac.rarity === 'legendary' ? '#f59e0b' :
                                ac.rarity === 'anomalous' ? '#a855f7' :
                                ac.rarity === 'relic' ? '#cd7f32' :
                                '#3b82f6'
                              }`
                            }}
                          >
                            {ac.rarity}
                          </div>
                        )}
                      </div>

                      {/* Aircraft Stats */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4" style={{ color: '#737373' }} />
                          <span style={{ color: '#a3a3a3' }}>{specs.capacity || ac.capacity || '?'} {t('fleet.pax')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Gauge className="w-4 h-4" style={{ color: '#737373' }} />
                          <span style={{ color: '#a3a3a3' }}>{specs.range || ac.range || '?'} {t('fleet.nm')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Fuel className="w-4 h-4" style={{ color: '#737373' }} />
                          <span style={{ color: '#a3a3a3' }}>{specs.fuelBurn || '?'} {t('fleet.lbHr')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Wrench className="w-4 h-4" style={{ color: '#737373' }} />
                          <span style={{ color: '#a3a3a3' }}>{ac.condition || t('fleet.good')}</span>
                        </div>
                      </div>

                      {/* Status Row */}
                      <div className="flex items-center justify-between py-2 border-t" style={{ borderColor: '#1a1a1a' }}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: ac.status === 'available' ? '#22c55e' :
                                              ac.status === 'in_flight' ? '#3b82f6' : '#f59e0b'
                            }}
                          />
                          <span className="text-sm" style={{ color: '#a3a3a3' }}>
                            {ac.status === 'available' ? t('fleet.available') :
                             ac.status === 'in_flight' ? t('fleet.inFlight') :
                             ac.status === 'maintenance' ? t('fleet.maintenance') : ac.status || t('fleet.available')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-sm" style={{ color: '#737373' }}>
                          <MapPin className="w-3 h-3" />
                          {ac.location || ac.homeBase || 'KPDX'}
                        </div>
                      </div>

                      {/* Lease Info */}
                      {ac.isLeased && (
                        <div className="mt-2 p-2 rounded text-sm" style={{ backgroundColor: '#1a1a0a' }}>
                          <div className="flex items-center justify-between">
                            <span style={{ color: '#f59e0b' }}>
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {t('fleet.monthsRemaining', { count: leaseInfo?.monthsRemaining || ac.leaseTermMonths || '?' })}
                            </span>
                            <span style={{ color: '#737373' }}>
                              ${(ac.monthlyPayment || leaseInfo?.monthlyPayment || 0).toLocaleString()}{t('fleet.perMonth')}
                            </span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Browse Market Link */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => navigate("/AircraftMarket")}
            style={{ backgroundColor: '#f59e0b', color: '#000', padding: '1rem 2rem' }}
          >
            <ShoppingCart className="w-5 h-5 mr-2" /> {t('fleet.browseMarket')}
          </Button>
        </div>

      </div>
    </div>
  );
}